import { useState, type ReactNode } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { C2Button } from '../components/c2';
import type { PastelColor } from '../components/c2/Pill';
import AuthShell from '../components/auth/AuthShell';
import AuthInput from '../components/auth/Input';

const COMPANIES = [
  'Airbnb', 'Block', 'Coinbase', 'Dropbox', 'Figma', 'Google', 'LinkedIn',
  'Meta', 'Netflix', 'Palantir', 'Pinterest', 'Salesforce', 'Snap', 'Spotify',
  'Stripe', 'Twitter / X', 'Uber', 'Other',
];

const pastelBg: Record<PastelColor, string> = {
  peach: 'var(--color-peach)',
  mint: 'var(--color-mint)',
  sky: 'var(--color-sky)',
  lemon: 'var(--color-lemon)',
  lilac: 'var(--color-lilac)',
};
const pastelDeep: Record<PastelColor, string> = {
  peach: 'var(--color-peach-deep)',
  mint: 'var(--color-mint-deep)',
  sky: 'var(--color-sky-deep)',
  lemon: 'var(--color-lemon-deep)',
  lilac: 'var(--color-lilac-deep)',
};

interface OptionProps {
  color: PastelColor;
  title: string;
  subtitle: string;
  icon: ReactNode;
  onClick: () => void;
}

function Option({ color, title, subtitle, icon, onClick }: OptionProps) {
  return (
    <button
      onClick={onClick}
      className="cursor-pointer flex flex-col items-center gap-3 transition-[border-color,transform,box-shadow] duration-150 text-ink"
      style={{
        background: pastelBg[color],
        border: '2px solid transparent',
        borderRadius: 20,
        padding: '32px 20px',
      }}
      onMouseEnter={e => {
        e.currentTarget.style.borderColor = 'var(--accent-solid)';
        e.currentTarget.style.transform = 'translateY(-2px)';
        e.currentTarget.style.boxShadow = 'var(--shadow-md)';
      }}
      onMouseLeave={e => {
        e.currentTarget.style.borderColor = 'transparent';
        e.currentTarget.style.transform = 'translateY(0)';
        e.currentTarget.style.boxShadow = 'none';
      }}
    >
      <div style={{ color: pastelDeep[color] }}>{icon}</div>
      <div className="font-display font-extrabold" style={{ fontSize: 28, letterSpacing: '-0.02em' }}>{title}</div>
      <div className="text-[15px] text-ink-soft">{subtitle}</div>
    </button>
  );
}

export default function GatekeeperPage() {
  const navigate = useNavigate();
  const [step, setStep] = useState<'question' | 'yes' | 'no'>('question');
  const [company, setCompany] = useState('');
  const [otherCompany, setOtherCompany] = useState('');

  if (step === 'question') {
    return (
      <AuthShell maxWidth={560}>
        <h2
          className="font-display font-extrabold text-ink m-0 mb-8 text-center"
          style={{ fontSize: 34, letterSpacing: '-0.02em' }}
        >
          Have you used go/ links before?
        </h2>
        <div className="grid grid-cols-2 gap-3.5">
          <Option
            color="mint"
            title="Yes"
            subtitle="I've used go/ links"
            icon={
              <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><path d="m9 12 2 2 4-4"/></svg>
            }
            onClick={() => setStep('yes')}
          />
          <Option
            color="sky"
            title="No"
            subtitle="I'm new to go/ links"
            icon={
              <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><path d="M12 8v8M8 12h8"/></svg>
            }
            onClick={() => setStep('no')}
          />
        </div>
        <div className="text-center mt-6 text-sm text-ink-soft">
          Already have an account?{' '}
          <Link to="/login" className="font-semibold no-underline" style={{ color: 'var(--accent-solid)' }}>Sign in</Link>
        </div>
      </AuthShell>
    );
  }

  if (step === 'yes') {
    const ready = !!company && (company !== 'Other' || !!otherCompany.trim());
    return (
      <AuthShell maxWidth={520}>
        <h2
          className="font-display font-extrabold text-ink m-0 mb-2.5 text-center"
          style={{ fontSize: 30, letterSpacing: '-0.02em' }}
        >
          Where have you used go/ links?
        </h2>
        <p className="text-[17px] text-ink-soft m-0 mb-7 leading-[1.55] text-center">
          (We're simply asking because we're curious about how many companies have go/ links.)
        </p>
        <select
          value={company}
          onChange={e => setCompany(e.target.value)}
          className="w-full outline-none cursor-pointer mb-3"
          style={{
            padding: '13px 16px',
            borderRadius: 14,
            border: '1px solid var(--color-hairline-strong)',
            background: 'var(--color-surface)',
            fontSize: 15,
            color: company ? 'var(--color-ink)' : 'var(--color-ink-muted)',
            fontFamily: 'inherit',
          }}
        >
          <option value="">Select a company...</option>
          {COMPANIES.map(c => (
            <option key={c} value={c} style={{ color: 'var(--color-ink)' }}>{c}</option>
          ))}
        </select>
        {company === 'Other' && (
          <AuthInput
            type="text"
            placeholder="Enter your company name"
            value={otherCompany}
            onChange={e => setOtherCompany(e.target.value)}
            autoFocus
          />
        )}
        <C2Button
          variant="accent"
          size="lg"
          className="w-full mt-2"
          style={{ opacity: ready ? 1 : 0.5, pointerEvents: ready ? 'auto' : 'none' }}
          onClick={() => navigate('/register')}
        >
          Continue to Sign Up →
        </C2Button>
        <button
          onClick={() => setStep('question')}
          className="w-full bg-transparent border-0 cursor-pointer text-[13px] text-ink-soft"
          style={{ padding: '16px 0 0' }}
        >
          ← Back
        </button>
      </AuthShell>
    );
  }

  // step === 'no'
  return (
    <AuthShell maxWidth={540}>
      <h2
        className="font-display font-extrabold text-ink m-0 mb-2.5 text-center"
        style={{ fontSize: 32, letterSpacing: '-0.02em', lineHeight: 1.1 }}
      >
        No problem! The water's fine.
      </h2>
      <p className="text-[17px] text-ink-soft m-0 mb-6 leading-[1.55] text-center">
        go/ links are easy to pick up — you'll be a pro in minutes.
      </p>
      <div
        className="rounded-lg flex gap-3.5 items-start mb-6"
        style={{ background: 'var(--color-lemon)', padding: '18px 20px' }}
      >
        <div className="text-2xl">💡</div>
        <div className="text-sm leading-[1.55] text-ink">
          <strong>One heads-up:</strong> once you sign up, go/ links won't work until you install and log into the Chrome extension. We'll walk you through it.
        </div>
      </div>
      <C2Button variant="accent" size="lg" className="w-full" onClick={() => navigate('/register')}>
        Continue to Sign Up →
      </C2Button>
      <button
        onClick={() => setStep('question')}
        className="w-full bg-transparent border-0 cursor-pointer text-[13px] text-ink-soft"
        style={{ padding: '16px 0 0' }}
      >
        ← Back
      </button>
    </AuthShell>
  );
}
